import { AppLayout } from '@/components/layouts/AppLayout'
import { DataTableRow } from '@/components/molecules/DataTableRow'
import { ListSearchInput, WorkshopSelect } from '@/components/molecules/ListFilters'
import { ListPagination } from '@/components/molecules/ListPagination'
import { SortableTableHead } from '@/components/molecules/SortableTableHead'
import { StatusBadges } from '@/components/molecules/StatusBadges'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useListParams } from '@/hooks/useListParams'
import { fetchCases } from '@/services/cases'
import type { EstimateListQuery, EstimateStatus, WorkshopLocation } from '@backend/schemas'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

type SortField = EstimateListQuery['sortField']

const STATUSES: Record<'active' | 'archived', EstimateStatus[]> = {
  active: ['processing', 'active'],
  archived: ['archived'],
}

const formatSek = (value: number) => value.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })

const Cases = () => {
  const navigate = useNavigate()
  const { params, update, setSort, searchParams } = useListParams<SortField>({ sortField: 'updatedAt', sortDir: 'desc' })
  const selectedStatus = searchParams.get('status') === 'archived' ? 'archived' : 'active'
  const query = {
    ...params,
    workshop: (params.workshop || undefined) as WorkshopLocation | undefined,
    status: STATUSES[selectedStatus],
  }

  const { data: cases, isLoading } = useQuery({
    queryKey: ['cases', query],
    queryFn: () => fetchCases(query),
    placeholderData: keepPreviousData,
  })

  const sortProps = {
    sortField: params.sortField,
    sortDir: params.sortDir,
    onSort: (field: SortField) => setSort(field, field === 'updatedAt' ? 'desc' : 'asc'),
    disabled: !!params.search,
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Delcheck</CardTitle>
            <div className="flex gap-4 mt-4">
              <ListSearchInput
                placeholder="Sök matchning (börjar med)..."
                value={params.search}
                onChange={search => update({ search })}
              />
              <WorkshopSelect value={params.workshop} onChange={workshop => update({ workshop })} />
              <Select value={selectedStatus} onValueChange={status => update({ status: status === 'active' ? null : status })}>
                <SelectTrigger className="max-w-[200px]">
                  <SelectValue placeholder="" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiva</SelectItem>
                  <SelectItem value="archived">Arkiverade</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead label="Matchning" field="id" {...sortProps} />
                      <SortableTableHead label="Verkstad" field="workshop" {...sortProps} />
                      <TableHead>Antal fakturor</TableHead>
                      <TableHead>Matchade rader</TableHead>
                      <TableHead>Antal avvikelser</TableHead>
                      <TableHead>Cabas totalt</TableHead>
                      <TableHead>Fakturor totalt</TableHead>
                      <SortableTableHead label="Senast uppdaterad" field="updatedAt" {...sortProps} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cases?.items.length ? (
                      cases.items.map(caseItem => (
                        <DataTableRow key={caseItem.id} onClick={() => navigate(`/cases/${caseItem.id}`)}>
                          <TableCell className="font-medium">{caseItem.id}</TableCell>
                          <TableCell className="capitalize">{caseItem.workshop}</TableCell>
                          <TableCell>{caseItem.invoices}</TableCell>
                          <TableCell>
                            {caseItem.matchedLines}/{caseItem.cabasLines}
                          </TableCell>
                          <TableCell>
                            <StatusBadges
                              error={caseItem.inconsistencies.error}
                              warning={caseItem.inconsistencies.warning}
                            />
                          </TableCell>
                          <TableCell>{formatSek(caseItem.cabasTotal)}</TableCell>
                          <TableCell>{formatSek(caseItem.invoiceTotal)}</TableCell>
                          <TableCell>{format(new Date(caseItem.updatedAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                        </DataTableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          {params.search || params.workshop ? 'Inga ärenden matchade sökningen' : 'Inga ärenden hittades'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <ListPagination
                  page={params.page}
                  pageSize={cases?.pageSize ?? 100}
                  total={cases?.total ?? 0}
                  itemLabel="ärenden"
                  onPageChange={page => update({ page: String(page) })}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}

export default Cases
